import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Save, ArrowLeft, Trash2, Upload } from 'lucide-react';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import RichEditor from '../components/RichEditor';

const AdminEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const contentType = searchParams.get('type') || 'script';
  const { scripts, articles, faqs, addScript, addArticle, addFaq, updateScript, updateArticle, updateFaq, deleteScript, deleteArticle, deleteFaq } = useData();
  const { user: currentUser, isAdmin } = useAuth();
  
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Performance',
    subcategory: '',
    content: '',
    author: 'LYSH DBA Team',
    readTime: '10 min read',
    question: '',
    answer: '',
    difficulty: 'Intermediate'
  });

  const isEditing = Boolean(id);

  useEffect(() => {
    if (isEditing) {
      const item = contentType === 'script' 
        ? scripts.find(s => s.id === id)
        : contentType === 'article' 
        ? articles.find(a => a.id === id)
        : faqs.find(f => f.id === id);
      if (item) {
        // Check if user can edit this content
        if (!isAdmin && item.authorId !== currentUser.id) {
          alert('You can only edit your own content');
          navigate('/admin');
          return;
        }
        setFormData({
          title: item.title || '',
          description: item.description || '',
          category: item.category,
          subcategory: item.subcategory,
          content: item.content || '',
          author: item.author || 'LYSH DBA Team',
          readTime: item.readTime || '10 min read',
          question: item.question || '',
          answer: item.answer || '',
          difficulty: item.difficulty || 'Intermediate'
        });
      }
    }
  }, [id, scripts, articles, faqs, isEditing, contentType, isAdmin, currentUser.id, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (isEditing) {
        if (contentType === 'script') {
          await updateScript(id, formData);
        } else if (contentType === 'article') {
          await updateArticle(id, formData);
        } else {
          await updateFaq(id, formData);
        }
      } else {
        if (contentType === 'script') {
          await addScript(formData, currentUser.id);
        } else if (contentType === 'article') {
          await addArticle(formData, currentUser.id);
        } else {
          await addFaq(formData, currentUser.id);
        }
      }
      
      navigate('/admin');
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Failed to save. Please try again.');
    }
  };

  const categories = {
    'Performance': ['Comprehensive', 'CPU', 'Memory', 'IO'],
    'Security': ['Permissions', 'Authentication', 'Encryption'],
    'High Availability': ['Always On', 'Backup', 'Replication'],
    'Backup and Recovery': ['Backup Strategies', 'Recovery Procedures', 'Backup Monitoring'],
    'Automation': ['Job Scheduling', 'PowerShell Scripts', 'Maintenance Plans'],
    'Installation': ['Fresh Installation', 'Configuration', 'Validation'],
    'Architecture and Configuration': ['System Design', 'Server Configuration', 'Network Setup'],
    'Missilience': ['Routine Maintenance', 'Index Maintenance', 'Health Monitoring'],
    'Azure SQL': ['Migration', 'Performance', 'Security']
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Reset subcategory when category changes
      ...(name === 'category' ? { subcategory: '' } : {})
    }));
  };



  const handleFileUpload = (file, type) => {
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      
      if (type === 'script') {
        setFormData(prev => ({ ...prev, content }));
      } else if (type === 'faq') {
        setFormData(prev => ({ ...prev, answer: content }));
      }
    };
    reader.readAsText(file);
  };

  const handleDelete = async () => {
    const itemType = contentType === 'script' ? 'script' : contentType === 'article' ? 'article' : 'FAQ';
    if (window.confirm(`Are you sure you want to delete this ${itemType}?`)) {
      try {
        console.log(`Deleting ${itemType} with ID:`, id);
        
        if (contentType === 'script') {
          await deleteScript(id);
        } else if (contentType === 'article') {
          await deleteArticle(id);
        } else {
          await deleteFaq(id);
        }
        
        console.log(`${itemType} deleted successfully`);
        navigate('/admin');
      } catch (error) {
        console.error('Failed to delete:', error);
        alert(`Failed to delete ${itemType}. Error: ${error.message}`);
      }
    }
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={() => navigate('/admin')} className="btn btn-secondary">
            <ArrowLeft size={16} />
            Back to Dashboard
          </button>
          <h1 className="page-title">
            {isEditing ? `Edit ${contentType === 'script' ? 'Script' : contentType === 'article' ? 'Article' : 'FAQ'}` : `Add New ${contentType === 'script' ? 'Script' : contentType === 'article' ? 'Article' : 'FAQ'}`}
          </h1>
        </div>
        
        {isEditing && (
          <button onClick={handleDelete} className="btn btn-danger">
            <Trash2 size={16} />
            Delete {contentType === 'script' ? 'Script' : contentType === 'article' ? 'Article' : 'FAQ'}
          </button>
        )}
      </div>

      <div style={{ background: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.07)' }}>
        <form onSubmit={handleSubmit}>
          {contentType === 'faq' ? (
            <div className="form-group">
              <label className="form-label">Question</label>
              <textarea
                name="question"
                className="form-textarea"
                value={formData.question}
                onChange={handleInputChange}
                required
                placeholder="Enter the FAQ question"
                rows="2"
              />
            </div>
          ) : (
            <div className="form-group">
              <label className="form-label">{contentType === 'script' ? 'Script' : 'Article'} Title</label>
              <input
                type="text"
                name="title"
                className="form-input"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder={`Enter ${contentType} title`}
              />
            </div>
          )}

          {contentType !== 'faq' && (
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                name="description"
                className="form-textarea"
                value={formData.description}
                onChange={handleInputChange}
                required
                placeholder={`Enter ${contentType} description`}
                rows="3"
              />
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: contentType === 'faq' ? '1fr 1fr 1fr' : '1fr 1fr', gap: '20px' }}>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select
                name="category"
                className="form-input"
                value={formData.category}
                onChange={handleInputChange}
                required
              >
                {Object.keys(categories).map(category => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Subcategory</label>
              <select
                name="subcategory"
                className="form-input"
                value={formData.subcategory}
                onChange={handleInputChange}
                required
              >
                <option value="">Select subcategory</option>
                {categories[formData.category]?.map(subcategory => (
                  <option key={subcategory} value={subcategory}>
                    {subcategory}
                  </option>
                ))}
              </select>
            </div>

            {contentType === 'faq' && (
              <div className="form-group">
                <label className="form-label">Difficulty</label>
                <select
                  name="difficulty"
                  className="form-input"
                  value={formData.difficulty}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                </select>
              </div>
            )}
          </div>

          {contentType === 'article' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div className="form-group">
                <label className="form-label">Author</label>
                <input
                  type="text"
                  name="author"
                  className="form-input"
                  value={formData.author}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter author name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Read Time</label>
                <input
                  type="text"
                  name="readTime"
                  className="form-input"
                  value={formData.readTime}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., 10 min read"
                />
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">
              {contentType === 'script' ? 'SQL Script Content' : contentType === 'article' ? 'Article Content' : 'Answer'}
            </label>
            
            {contentType !== 'article' && (
              <div className="upload-section" style={{ marginBottom: '15px' }}>
                <input
                  type="file"
                  accept=".sql,.txt,.md"
                  onChange={(e) => handleFileUpload(e.target.files[0], contentType)}
                  className="file-input"
                  id={`${contentType}-upload`}
                  style={{ display: 'none' }}
                />
                <label htmlFor={`${contentType}-upload`} className="btn btn-secondary">
                  <Upload size={16} />
                  Upload {contentType === 'script' ? 'SQL' : 'Text'} File
                </label>
                <button 
                  type="button" 
                  onClick={() => setShowGuidelines(!showGuidelines)} 
                  className="btn btn-info"
                >
                  📋 Format Guidelines
                </button>
              </div>
            )}
            
            {showGuidelines && contentType !== 'article' && (
              <div className="guidelines-panel" style={{ marginBottom: '15px' }}>
                <h4>{contentType === 'script' ? 'SQL Script' : 'FAQ'} Format Guidelines</h4>
                
                {contentType === 'script' ? (
                  <div className="format-section">
                    <h5>📄 SQL Script Files (.sql, .txt)</h5>
                    <div className="format-rules">
                      <strong>File Structure:</strong>
                      <code>-- Script Title and Description</code><br/>
                      <code>-- Author: Your Name</code><br/>
                      <code>-- Purpose: What this script does</code><br/><br/>
                      
                      <strong>SQL Best Practices:</strong>
                      • Use clear comments with --<br/>
                      • Include error handling where needed<br/>
                      • Use proper indentation<br/>
                      • Test scripts before uploading<br/>
                      • Include sample output if helpful<br/><br/>
                      
                      <strong>Example Format:</strong>
                      <code>-- CPU Usage Monitoring Script</code><br/>
                      <code>-- Identifies high CPU consuming queries</code><br/>
                      <code></code><br/>
                      <code>SELECT TOP 10</code><br/>
                      <code>    qs.sql_handle,</code><br/>
                      <code>    qs.total_worker_time</code><br/>
                      <code>FROM sys.dm_exec_query_stats qs;</code>
                    </div>
                  </div>
                ) : (
                  <div className="format-section">
                    <h5>❓ FAQ Content Files (.txt, .md)</h5>
                    <div className="format-rules">
                      <strong>Text Format:</strong>
                      • Use clear, concise language<br/>
                      • Include step-by-step instructions<br/>
                      • Add code examples where relevant<br/>
                      • Use bullet points for lists<br/><br/>
                      
                      <strong>Markdown Support:</strong>
                      <code>**Bold text**</code><br/>
                      <code>*Italic text*</code><br/>
                      <code>`code snippets`</code><br/>
                      <code>- Bullet points</code><br/>
                      <code>1. Numbered lists</code><br/><br/>
                      
                      <strong>SQL in FAQ:</strong>
                      <code>```sql</code><br/>
                      <code>SELECT * FROM table;</code><br/>
                      <code>```</code>
                    </div>
                  </div>
                )}
                
                <div className="format-section">
                  <h5>✅ Best Practices</h5>
                  <div className="format-rules">
                    • Keep content focused and relevant<br/>
                    • Use proper grammar and spelling<br/>
                    • Test all code before submitting<br/>
                    • Include context and explanations<br/>
                    • Follow naming conventions<br/>
                    • Add appropriate difficulty level
                  </div>
                </div>
              </div>
            )}
            
            {contentType === 'article' ? (
              <RichEditor
                content={formData.content}
                onChange={(blocks) => setFormData(prev => ({ ...prev, content: blocks }))}
              />
            ) : (
              <textarea
                name={contentType === 'faq' ? 'answer' : 'content'}
                className="form-textarea"
                value={contentType === 'faq' ? formData.answer : formData.content}
                onChange={handleInputChange}
                required
                placeholder={contentType === 'script' ? 'Enter your SQL script here...' : contentType === 'faq' ? 'Enter the detailed answer...' : 'Enter your article content here...'}
                rows={contentType === 'faq' ? '10' : '20'}
                style={contentType === 'script' ? { fontFamily: 'Consolas, Monaco, "Courier New", monospace', fontSize: '0.9rem' } : {}}
              />
            )}
          </div>

          <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => navigate('/admin')} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              <Save size={16} />
              {isEditing ? `Update ${contentType === 'script' ? 'Script' : contentType === 'article' ? 'Article' : 'FAQ'}` : `Save ${contentType === 'script' ? 'Script' : contentType === 'article' ? 'Article' : 'FAQ'}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminEditor;